<template>
  <div class="cathay-deck-entry" @click.stop @keydown.stop>
    <button type="button" class="resource-action cathay-deck-trigger" aria-haspopup="dialog" @click="open">
      <span>View pitch deck</span>
      <svg class="cathay-deck-icon" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <path d="M13 4h17M14.5 4v13.5H29V4M13 17.5h17M22 17.5V28m-4.5-1 4.5-5 4.5 5" />
        <circle cx="6" cy="7.5" r="2.5" />
        <path d="M3 21v-5a3 3 0 0 1 3-3h2v15H4.5V17.5M6.25 22.5V28" />
        <path class="cathay-presenter-arm" d="m8 13 1 7" />
        <g class="cathay-presentation-bulb" transform="translate(0 1.2)">
          <path class="cathay-bulb-glass" d="M20.5 12c0-1.1-1.5-1.4-1.5-3a3 3 0 1 1 6 0c0 1.6-1.5 1.9-1.5 3Z" />
          <path d="M21 14h2" />
          <path class="cathay-bulb-rays" d="M17.2 8.5h-1M26.8 8.5h1M18.5 5l-.7-.7M25.5 5l.7-.7" />
        </g>
      </svg>
    </button>
    <Teleport to="body">
      <dialog ref="viewer" class="cathay-deck-viewer" aria-label="Cathay Flex pitch deck" @close="onClose" @click.stop="backdrop" @keydown.stop>
        <header class="cathay-deck-header">
          <div class="cathay-deck-heading">Cathay Flex<small>Pitch deck, 16 slides</small></div>
          <div class="cathay-deck-tools">
            <button type="button" aria-label="Zoom out" :disabled="zoom <= 1" @click="changeZoom(zoom / 2)"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14" /></svg></button>
            <button type="button" class="cathay-deck-fit" aria-label="Fit all slides" @click="changeZoom(1)">Fit</button>
            <button type="button" aria-label="Zoom in" :disabled="zoom >= maxZoom" @click="changeZoom(zoom * 2)"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M12 5v14" /></svg></button>
            <a :href="download" download="Cathay-Flex-contact-sheet.png" aria-label="Download high-resolution contact sheet"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12m-4-4 4 4 4-4M4 16v4h16v-4" /></svg></a>
          </div>
          <button type="button" class="cathay-deck-close" aria-label="Close pitch deck" autofocus @click="close"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg></button>
        </header>
        <div ref="viewport" class="cathay-deck-viewport" tabindex="0" aria-label="Slide overview. Use the zoom controls to read slides, then scroll to move around.">
          <div class="cathay-deck-canvas" :style="canvasStyle">
            <img v-if="isOpen" ref="sheet" :src="source" width="8000" height="4896" :style="imageStyle" alt="All 16 Cathay Flex slides covering the opportunity, service experience, functional design and business value" draggable="false" @click="zoomAt" @load="loaded = true" @error="failed = true">
          </div>
          <p v-if="!loaded && !failed" class="cathay-deck-status" role="status">Loading the deck…</p>
          <p v-if="failed" class="cathay-deck-status" role="status">The preview could not load. <a :href="download">Open the full-resolution sheet</a>.</p>
        </div>
      </dialog>
    </Teleport>
  </div>
</template>

<script>
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import '../../assets/css/resource-action.css';

const ratio = 8000 / 4896;
export default {
  name: 'CathayFlexDeck',
  data() {
    return { isOpen: false, loaded: false, failed: false, zoom: 1, fitWidth: 800,
      source: '/assets/deck/cathay-flex/contact-sheet.webp?v=c081136deaec',
      download: '/assets/deck/cathay-flex/Cathay-Flex-contact-sheet.png?v=c081136deaec' };
  },
  computed: {
    maxZoom() { return Math.min(16, 8000 / this.fitWidth); },
    imageStyle() { return { width: `${this.fitWidth * this.zoom}px`, cursor: this.zoom === 1 ? 'zoom-in' : 'zoom-out' }; },
    canvasStyle() { return { width: `${this.fitWidth * this.zoom + 32}px`, height: `${this.fitWidth * this.zoom / ratio + 32}px` }; },
  },
  beforeUnmount() { this.onClose(); },
  methods: {
    async open() {
      this._trigger = document.activeElement;
      this.zoom = 1;
      this.failed = false;
      this.loaded = false;
      this.isOpen = true;
      this._releaseScroll = lockBodyScroll();
      this.$refs.viewer.showModal();
      await this.$nextTick();
      this.measure();
      this._resize = new ResizeObserver(this.measure);
      this._resize.observe(this.$refs.viewport);
    },
    measure() {
      const viewport = this.$refs.viewport;
      this.fitWidth = Math.max(100, Math.min(viewport.clientWidth - 32, (viewport.clientHeight - 32) * ratio));
    },
    close() { this.$refs.viewer.close(); },
    onClose() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this._resize?.disconnect();
      this._releaseScroll?.();
      this._releaseScroll = null;
      if (this._trigger?.isConnected) this._trigger.focus({ preventScroll: true });
    },
    backdrop(event) { if (event.target === this.$refs.viewer) this.close(); },
    zoomAt(event) { this.changeZoom(this.zoom === 1 ? 4 : 1, event); },
    async changeZoom(value, event) {
      const viewport = this.$refs.viewport;
      const image = this.$refs.sheet;
      if (!image) return;
      const bounds = viewport.getBoundingClientRect();
      const before = image.getBoundingClientRect();
      const x = event ? event.clientX - bounds.left : viewport.clientWidth / 2;
      const y = event ? event.clientY - bounds.top : viewport.clientHeight / 2;
      const rx = Math.max(0, Math.min(1, (bounds.left + x - before.left) / before.width));
      const ry = Math.max(0, Math.min(1, (bounds.top + y - before.top) / before.height));
      this.zoom = Math.max(1, Math.min(this.maxZoom, value));
      await this.$nextTick();
      const after = image.getBoundingClientRect();
      viewport.scrollLeft += after.left + rx * after.width - bounds.left - x;
      viewport.scrollTop += after.top + ry * after.height - bounds.top - y;
    },
  },
};
</script>

<style>
.cathay-deck-entry { display: block; }
.cathay-deck-trigger svg, .cathay-deck-viewer svg { display: block; width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.35; stroke-linecap: round; stroke-linejoin: round; }
.mono-home .cathay-deck-trigger::before { display: none; }
.cathay-deck-trigger .cathay-deck-icon { flex: 0 0 26px; width: 26px; height: 26px; margin-block: -5px; stroke-width: 1.65; overflow: visible; }
.cathay-presenter-arm { transform-origin: 8px 13px; transition: transform 240ms cubic-bezier(.22, 1, .36, 1); }
.cathay-presentation-bulb { opacity: .5; transition: color 180ms ease, opacity 180ms ease; }
.cathay-bulb-glass { fill: currentColor; fill-opacity: 0; transition: fill-opacity 180ms ease; }
.cathay-bulb-rays { opacity: 0; transition: opacity 180ms ease; }
.mono-home .cathay-deck-trigger { color: inherit; font-family: inherit; font-weight: inherit; min-height: 0; transition: none; }
.cathay-deck-trigger:focus-visible .cathay-presenter-arm { transform: rotate(-80deg); transition: none; }
.cathay-deck-trigger:focus-visible .cathay-presentation-bulb { color: #ad7c21; opacity: 1; transition: none; }
.cathay-deck-trigger:focus-visible .cathay-bulb-glass { fill-opacity: .2; transition: none; }
.cathay-deck-trigger:focus-visible .cathay-bulb-rays { opacity: 1; transition: none; }
@media (hover: hover) and (pointer: fine) {
  .cathay-deck-trigger:hover .cathay-presenter-arm { transform: rotate(-80deg); }
  .cathay-deck-trigger:hover .cathay-presentation-bulb { color: #ad7c21; opacity: 1; }
  .cathay-deck-trigger:hover .cathay-bulb-glass { fill-opacity: .2; }
  .cathay-deck-trigger:hover .cathay-bulb-rays { opacity: 1; }
  body.dark-mode .cathay-deck-trigger:hover .cathay-presentation-bulb { color: #ead28c; }
}
body.dark-mode .cathay-deck-trigger:focus-visible .cathay-presentation-bulb { color: #ead28c; }
@media (prefers-reduced-motion: reduce) {
  .cathay-deck-trigger .cathay-presenter-arm, .cathay-presentation-bulb, .cathay-bulb-glass, .cathay-bulb-rays { transition: none; }
}
.cathay-deck-viewer { position: fixed; inset: 0; box-sizing: border-box; margin: auto; width: calc(100vw - 48px); height: calc(100dvh - 48px); max-width: 1800px; max-height: none; padding: 0; border: 1px solid rgb(120 125 120 / 25%); border-radius: 12px; color: #303633; background: #f7f8f6; overflow: hidden; box-shadow: 0 24px 90px rgb(0 0 0 / 25%); }
.cathay-deck-viewer[open] { display: flex; flex-direction: column; }
.cathay-deck-viewer::backdrop { background: rgb(20 25 23 / 72%); }
.cathay-deck-header { display: flex; flex-shrink: 0; align-items: center; gap: 20px; padding: 12px 16px 12px 24px; border-bottom: 1px solid rgb(120 125 120 / 16%); font: 14px/1.3 'Quattrocento Sans', sans-serif; }
.cathay-deck-heading { margin-right: auto; font-weight: 700; }
.cathay-deck-heading small { display: block; margin-top: 4px; font-size: 12px; font-weight: 400; opacity: .65; }
.cathay-deck-tools { display: flex; gap: 2px; align-items: center; }
.cathay-deck-header button, .cathay-deck-header a { display: grid; place-items: center; box-sizing: border-box; width: 44px; min-width: 44px; height: 44px; padding: 0; background: none; border: 0; border-radius: 6px; color: inherit; font: inherit; text-decoration: none; cursor: pointer; }
.cathay-deck-header button:disabled { opacity: .25; cursor: default; }
.cathay-deck-header :is(button, a):focus-visible, .cathay-deck-viewport:focus-visible { outline: 2px solid #427268; outline-offset: -3px; }
.cathay-deck-viewport { position: relative; flex: 1; min-height: 0; overflow: auto; overscroll-behavior: contain; background: #e7ece9; }
.cathay-deck-canvas { box-sizing: border-box; display: grid; place-items: center; min-width: 100%; min-height: 100%; padding: 16px; }
.cathay-deck-canvas img { display: block; flex-shrink: 0; max-width: none; height: auto; margin: 0; border-radius: 0; user-select: none; -webkit-user-select: none; }
.cathay-deck-status { position: absolute; top: 40%; left: 24px; right: 24px; text-align: center; font: 15px/1.5 'Quattrocento Sans', sans-serif; }
body.dark-mode .cathay-deck-viewer { background: #252b28; color: #e3e8e4; }
body.dark-mode .cathay-deck-viewport { background: #1b211e; }
@media (hover: hover) and (pointer: fine) { .cathay-deck-header :is(button, a):not(:disabled):hover { background: rgb(120 125 120 / 12%); } }
@media (max-width: 600px) {
  .cathay-deck-viewer { width: 100vw; height: 100dvh; border: 0; border-radius: 0; }
  .cathay-deck-header { gap: 4px; padding: 8px; }
  .cathay-deck-heading { padding-left: 4px; font-size: 13px; }
  .cathay-deck-heading small { font-size: 11px; }
  .cathay-deck-tools { gap: 0; }
}
@media (max-width: 380px) {
  .cathay-deck-header { flex-wrap: wrap; }
  .cathay-deck-tools { order: 3; width: 100%; justify-content: center; }
}
</style>
